'use client';
import { useEffect, useState } from 'react';
import { useT } from '@/lib/useT';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import {
  Building2, Users, Truck, Package, Activity,
  BarChart3, Heart, Plus, Megaphone, Clock,
  Server, X,
} from 'lucide-react';

interface PlatformStats {
  totalOrgs: number;
  activeOrgs: number;
  totalUsers: number;
  totalVehicles: number;
  totalOrders: number;
  planDistribution: { plan: string; count: number }[];
}

interface SystemHealth {
  uptime: number;
  memory: { rss: number; heapUsed: number; heapTotal: number };
  nodeVersion: string;
  dbConnected: boolean;
  auditToday: number;
  apiVersion: string;
}

interface AuditEntry {
  id: string;
  action: string;
  entity: string;
  entityId?: string;
  timestamp: string;
  user: { name: string; surname: string; email: string };
  org?: { id: string; name: string; slug: string } | null;
}

const PLAN_COLORS: Record<string, string> = {
  FREE: '#6B7280',
  STARTER: '#FFD60A',
  BUSINESS: '#F59E0B',
  ENTERPRISE: '#8B5CF6',
};

function formatUptime(seconds: number) {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export default function SuperAdminDashboardPage() {
  const t = useT();
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [health, setHealth] = useState<SystemHealth | null>(null);
  const [logs, setLogs] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);

  // Org form
  const [orgForm, setOrgForm] = useState({
    name: '', slug: '', ownerEmail: '', ownerName: '', ownerSurname: '', ownerPassword: '', plan: 'FREE', country: 'DE', seedDemo: true,
  });
  const [orgSaving, setOrgSaving] = useState(false);

  // Announcement form
  const [annForm, setAnnForm] = useState({ title: '', message: '', type: 'INFO' as 'INFO' | 'WARNING' | 'ALERT' });
  const [annSaving, setAnnSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/super-admin/stats').catch(() => null),
      api.get('/super-admin/health').catch(() => null),
      api.get('/super-admin/logs?limit=20').catch(() => []),
    ]).then(([s, h, l]) => {
      if (s) setStats(s);
      if (h) setHealth(h);
      if (Array.isArray(l)) setLogs(l);
    }).finally(() => setLoading(false));
  }, []);

  const kpis = [
    { label: 'Total Organizations', value: stats?.totalOrgs ?? 0, icon: Building2, accent: 'var(--c-accent)' },
    { label: 'Active Organizations', value: stats?.activeOrgs ?? 0, icon: Activity, accent: '#14A76C' },
    { label: 'Total Users', value: stats?.totalUsers ?? 0, icon: Users, accent: '#FFD60A' },
    { label: 'Total Vehicles', value: stats?.totalVehicles ?? 0, icon: Truck, accent: '#F59E0B' },
    { label: 'Total Orders', value: stats?.totalOrders ?? 0, icon: Package, accent: '#8B5CF6' },
  ];

  const maxPlanCount = Math.max(...(stats?.planDistribution?.map(p => p.count) || [1]));

  async function handleCreateOrg(e: React.FormEvent) {
    e.preventDefault();
    setOrgSaving(true);
    try {
      await api.post('/super-admin/organizations', orgForm);
      toast.success('Organisation erstellt');
      setShowOrgModal(false);
      setOrgForm({ name: '', slug: '', ownerEmail: '', ownerName: '', ownerSurname: '', ownerPassword: '', plan: 'FREE', country: 'DE', seedDemo: true });
      // Refresh stats
      const s = await api.get('/super-admin/stats');
      setStats(s);
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Erstellen');
    } finally {
      setOrgSaving(false);
    }
  }

  async function handleSendAnnouncement(e: React.FormEvent) {
    e.preventDefault();
    setAnnSaving(true);
    try {
      const res = await api.post('/super-admin/announcements', annForm);
      toast.success(`Ankuendigung an ${res.count} Benutzer gesendet`);
      setShowAnnouncementModal(false);
      setAnnForm({ title: '', message: '', type: 'INFO' });
    } catch (err: any) {
      toast.error(err.message || 'Fehler beim Senden');
    } finally {
      setAnnSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold uppercase tracking-wide" style={{ color: 'var(--c-heading)' }}>
            Platform Overview
          </h1>
          <p className="label mt-1">{t('sa_dashboard')}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3 flex-wrap">
        <button
          onClick={() => setShowOrgModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: 'var(--c-accent)', color: '#0A0A0A' }}
        >
          <Plus size={16} />
          Neue Organisation
        </button>
        <button
          onClick={() => setShowAnnouncementModal(true)}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
          style={{ background: '#8B5CF6', color: '#fff' }}
        >
          <Megaphone size={16} />
          Ankuendigung senden
        </button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="logixa-card p-5">
              {loading ? (
                <div className="animate-pulse space-y-3">
                  <div className="h-3 rounded w-2/3" style={{ background: 'var(--c-hover)' }} />
                  <div className="h-8 rounded w-1/2" style={{ background: 'var(--c-hover)' }} />
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ background: `${kpi.accent}20`, color: kpi.accent }}
                    >
                      <Icon size={16} />
                    </div>
                    <span className="text-xs font-medium" style={{ color: 'var(--c-muted)' }}>
                      {kpi.label}
                    </span>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: 'var(--c-heading)' }}>
                    {kpi.value.toLocaleString()}
                  </p>
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* System Health + Plan Distribution row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* System Health */}
        <div className="logixa-card p-5">
          <div className="flex items-center gap-3 mb-5">
            <Heart size={20} color="#14A76C" />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--c-heading)' }}>
              System Health
            </h2>
          </div>
          {loading || !health ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-6 rounded" style={{ background: 'var(--c-hover)' }} />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'var(--c-border)' }}>
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>
                  <Clock size={14} className="inline mr-2" />Uptime
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--c-heading)' }}>{formatUptime(health.uptime)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'var(--c-border)' }}>
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>
                  <Server size={14} className="inline mr-2" />Memory (Heap)
                </span>
                <span className="text-sm font-semibold" style={{ color: 'var(--c-heading)' }}>
                  {health.memory.heapUsed} / {health.memory.heapTotal} MB
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'var(--c-border)' }}>
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>Node.js</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--c-heading)' }}>{health.nodeVersion}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b" style={{ borderColor: 'var(--c-border)' }}>
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>Database</span>
                <span className="text-sm font-semibold" style={{ color: health.dbConnected ? '#14A76C' : '#EF4444' }}>
                  {health.dbConnected ? 'Connected' : 'Error'}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm" style={{ color: 'var(--c-muted)' }}>API Version</span>
                <span className="text-sm font-semibold" style={{ color: 'var(--c-heading)' }}>{health.apiVersion}</span>
              </div>
            </div>
          )}
        </div>

        {/* Plan Distribution */}
        <div className="logixa-card p-5">
          <div className="flex items-center gap-3 mb-5">
            <BarChart3 size={20} color="var(--c-accent)" />
            <h2 className="text-lg font-semibold" style={{ color: 'var(--c-heading)' }}>
              Plan Distribution
            </h2>
          </div>

          {loading ? (
            <div className="space-y-3 animate-pulse">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-10 rounded-xl" style={{ background: 'var(--c-hover)' }} />
              ))}
            </div>
          ) : stats?.planDistribution && stats.planDistribution.length > 0 ? (
            <div className="space-y-3">
              {stats.planDistribution.map((p) => (
                <div key={p.plan} className="flex items-center gap-4">
                  <span
                    className="text-sm font-semibold w-24 shrink-0"
                    style={{ color: PLAN_COLORS[p.plan] || 'var(--c-text)' }}
                  >
                    {p.plan}
                  </span>
                  <div className="flex-1 h-8 rounded-lg overflow-hidden" style={{ background: 'var(--c-hover)' }}>
                    <div
                      className="h-full rounded-lg flex items-center px-3 transition-all duration-500"
                      style={{
                        width: `${Math.max((p.count / maxPlanCount) * 100, 8)}%`,
                        background: PLAN_COLORS[p.plan] || 'var(--c-accent)',
                      }}
                    >
                      <span className="text-xs font-bold text-white">{p.count}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--c-muted)' }}>
              No plan data available
            </p>
          )}
        </div>
      </div>

      {/* Recent Audit Logs */}
      <div className="logixa-card p-5">
        <div className="flex items-center gap-3 mb-5">
          <Activity size={20} color="var(--c-accent)" />
          <h2 className="text-lg font-semibold" style={{ color: 'var(--c-heading)' }}>
            Recent Audit Logs
          </h2>
        </div>
        {loading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-8 rounded" style={{ background: 'var(--c-hover)' }} />
            ))}
          </div>
        ) : logs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--c-border)' }}>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--c-muted)' }}>{t('sa_time')}</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--c-muted)' }}>{t('sa_user')}</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--c-muted)' }}>{t('sa_action')}</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--c-muted)' }}>{t('sa_entity')}</th>
                  <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--c-muted)' }}>{t('sa_org')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--c-border)' }}>
                    <td className="py-2 px-2" style={{ color: 'var(--c-muted)' }}>
                      {new Date(log.timestamp).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-2 px-2" style={{ color: 'var(--c-text)' }}>
                      {log.user.name} {log.user.surname}
                    </td>
                    <td className="py-2 px-2">
                      <span
                        className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
                        style={{ background: 'var(--c-accent)', color: '#0A0A0A', opacity: 0.9 }}
                      >
                        {log.action}
                      </span>
                    </td>
                    <td className="py-2 px-2" style={{ color: 'var(--c-text)' }}>
                      {log.entity}{log.entityId ? ` #${log.entityId.slice(0, 8)}` : ''}
                    </td>
                    <td className="py-2 px-2" style={{ color: 'var(--c-muted)' }}>
                      {log.org?.name || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm py-8 text-center" style={{ color: 'var(--c-muted)' }}>
            Keine Audit-Logs vorhanden
          </p>
        )}
      </div>

      {/* Create Organization Modal */}
      {showOrgModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="logixa-card p-6 w-full max-w-lg mx-4 relative" style={{ background: 'var(--c-panel)' }}>
            <button
              onClick={() => setShowOrgModal(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:opacity-70"
              style={{ color: 'var(--c-muted)' }}
            >
              <X size={18} />
            </button>
            <h2 className="text-xl font-bold mb-5" style={{ color: 'var(--c-heading)' }}>
              Neue Organisation erstellen
            </h2>
            <form onSubmit={handleCreateOrg} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_org_name')}</label>
                  <input
                    required
                    value={orgForm.name}
                    onChange={e => setOrgForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_slug')}</label>
                  <input
                    required
                    value={orgForm.slug}
                    onChange={e => setOrgForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_owner_fn')}</label>
                  <input
                    required
                    value={orgForm.ownerName}
                    onChange={e => setOrgForm(f => ({ ...f, ownerName: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_owner_ln')}</label>
                  <input
                    required
                    value={orgForm.ownerSurname}
                    onChange={e => setOrgForm(f => ({ ...f, ownerSurname: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_owner_email')}</label>
                <input
                  required
                  type="email"
                  value={orgForm.ownerEmail}
                  onChange={e => setOrgForm(f => ({ ...f, ownerEmail: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>{t('sa_owner_pw')}</label>
                <input
                  required
                  type="password"
                  value={orgForm.ownerPassword}
                  onChange={e => setOrgForm(f => ({ ...f, ownerPassword: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>Plan</label>
                  <select
                    value={orgForm.plan}
                    onChange={e => setOrgForm(f => ({ ...f, plan: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  >
                    <option value="FREE">FREE</option>
                    <option value="STARTER">STARTER</option>
                    <option value="BUSINESS">BUSINESS</option>
                    <option value="ENTERPRISE">ENTERPRISE</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>Land</label>
                  <input
                    value={orgForm.country}
                    onChange={e => setOrgForm(f => ({ ...f, country: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg text-sm"
                    style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm" style={{ color: 'var(--c-text)' }}>
                <input type="checkbox" checked={orgForm.seedDemo}
                  onChange={e => setOrgForm(f => ({ ...f, seedDemo: e.target.checked }))} />
                Mit Demo-Daten befüllen (3 Fahrzeuge, 2 Fahrer, 5 Aufträge, 1 Route)
              </label>

              <button
                type="submit"
                disabled={orgSaving}
                className="w-full py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: 'var(--c-accent)', color: '#0A0A0A' }}
              >
                {orgSaving ? 'Erstelle...' : 'Organisation erstellen'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Announcement Modal */}
      {showAnnouncementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="logixa-card p-6 w-full max-w-lg mx-4 relative" style={{ background: 'var(--c-panel)' }}>
            <button
              onClick={() => setShowAnnouncementModal(false)}
              className="absolute top-4 right-4 p-1 rounded-lg hover:opacity-70"
              style={{ color: 'var(--c-muted)' }}
            >
              <X size={18} />
            </button>
            <h2 className="text-xl font-bold mb-5" style={{ color: 'var(--c-heading)' }}>
              Ankuendigung senden
            </h2>
            <form onSubmit={handleSendAnnouncement} className="space-y-4">
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>Typ</label>
                <select
                  value={annForm.type}
                  onChange={e => setAnnForm(f => ({ ...f, type: e.target.value as any }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                >
                  <option value="INFO">INFO</option>
                  <option value="WARNING">WARNING</option>
                  <option value="ALERT">ALERT</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>Titel</label>
                <input
                  required
                  value={annForm.title}
                  onChange={e => setAnnForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block" style={{ color: 'var(--c-muted)' }}>Nachricht</label>
                <textarea
                  required
                  rows={4}
                  value={annForm.message}
                  onChange={e => setAnnForm(f => ({ ...f, message: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                  style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', color: 'var(--c-text)' }}
                />
              </div>
              <button
                type="submit"
                disabled={annSaving}
                className="w-full py-2.5 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
                style={{ background: '#8B5CF6', color: '#fff' }}
              >
                {annSaving ? 'Sende...' : 'An alle Benutzer senden'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
